/**
 * @param {number} x Координата на оси x.
 * @param {number} y Координата на оси y.
 */
interface IPoint {
  x: number;
  y: number;
}

/**
 * @param {IPoint} begin Координаты начала отрезка.
 * @param {IPoint} end Координаты конца отрезка.
 */
interface ILine {
  begin: IPoint;
  end: IPoint;
}

/**
 * Вычислить длину отрезка.
 *
 * @return {number} Длина отрезка.
 */
const getLineLength = ({ begin, end }: ILine): number => {
  const { x: x1, y: y1 } = begin;
  const { x: x2, y: y2 } = end;

  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
};

/**
 * Вычислить координаты середины отрезка.
 *
 * @return {IPoint} Координаты середины отрезка.
 */
const getLineMiddlePoint = ({ begin, end }: ILine): IPoint => {
  const { x: xBegin, y: yBegin } = begin;
  const { x: xEnd, y: yEnd } = end;

  const xM = (xBegin + xEnd) / 2;
  const yM = (yBegin + yEnd) / 2;

  return { x: xM, y: yM };
};

/**
 * Функция, позволяющая принять решение является ли линия частью окружности.
 *
 * @param {[number, number][]} linePoints Массив координат кривой линии.
 *
 * @return {boolean} Решение о том, является ли кривая частью круга.
 */
const isCurved = (linePoints: [number, number][]): boolean => {
  const begin = linePoints[0];
  const end = linePoints[linePoints.length - 1];

  const middlePoint = getLineMiddlePoint({
    begin: { x: begin[0], y: begin[1] },
    end: { x: end[0], y: end[1] },
  });

  const distanceBetweenMiddle = getLineLength({
    begin: middlePoint,
    end: { x: begin[0], y: begin[1] },
  });

  const koefs = [];
  for (let i = 0; i < linePoints.length; i++) {
    const point = linePoints[i];
    const distanceBetweenCenterAndPoint = getLineLength({
      begin: middlePoint,
      end: { x: point[0], y: point[1] },
    });

    const koef = distanceBetweenCenterAndPoint / distanceBetweenMiddle;

    // этой кривизны достаточно чтобы посчитать кривую хордой круга.
    if (koef >= 0.5 && koef <= 1.5) {
      koefs.push(koef);
    }
  }

  return koefs.length === 10;

};

export { getLineLength, getLineMiddlePoint, isCurved };
